import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GlobalContext } from "../../context/globalContext";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import {
  deleteSavedRegistration,
  getMySavedRegistrations,
  SavedRegistration,
} from "../../api/registration";

const STEP_LABELS: Record<number, string> = {
  1: "Your details",
  2: "Ticket selection",
  3: "Review & pay",
};

export default function UserPageDashboard() {
  const { currentUser } = useContext(GlobalContext);
  const [saved, setSaved] = useState<SavedRegistration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clearingSlug, setClearingSlug] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    setIsLoading(true);
    getMySavedRegistrations()
      .then((data) => {
        if (!cancelled) setSaved(data);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load saved registrations.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  async function handleStartOver(slug: string) {
    setClearingSlug(slug);
    try {
      await deleteSavedRegistration(slug);
      setSaved((prev) => prev.filter((s) => s.event.slug !== slug));
      window.location.href = `/events/${slug}/register?fresh=1`;
    } catch {
      setError("Could not clear saved registration.");
      setClearingSlug(null);
    }
  }

  return (
    <div>
      <h2 className="text-lg font-bold text-slate-900 mb-1">Dashboard</h2>
      <p className="text-sm text-slate-600 mb-6">
        Continue unfinished event registrations or manage your account from the
        menu.
      </p>

      <section>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">
          Saved registrations (not paid)
        </h3>

        {isLoading ? (
          <LoadingPanel
            title="Loading saved registrations"
            subtitle="Checking for in-progress registrations."
            className="py-8"
          />
        ) : error ? (
          <ErrorAlert>{error}</ErrorAlert>
        ) : saved.length === 0 ? (
          <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-4">
            You have no saved registrations. When you start registering for an
            event, your progress is saved automatically so you can finish
            later.
          </p>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  <th className="px-4 py-3 font-semibold text-slate-700">
                    Event
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700">
                    Last step
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700">
                    Updated
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {saved.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b border-slate-100 last:border-0"
                  >
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {item.event.title}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {STEP_LABELS[item.step] ?? `Step ${item.step}`}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {new Date(item.updatedAt).toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Link
                        to={`/events/${item.event.slug}/register?resume=1`}
                        className="eh-btn-primary inline-block px-4 py-1.5 text-xs font-semibold mr-2"
                      >
                        Continue
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleStartOver(item.event.slug)}
                        disabled={clearingSlug === item.event.slug}
                        className="eh-btn-secondary inline-block px-4 py-1.5 text-xs font-medium"
                      >
                        {clearingSlug === item.event.slug
                          ? "Clearing…"
                          : "Start over"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
